import languages from '@/data/languages.json'
import Cookie from 'js-cookie'
import type { JwtPayload } from 'jwt-decode'
import { jwtDecode } from 'jwt-decode'

const isLocal = window.location.hostname === 'localhost'
const prefix = 'TokenAUID'
const refreshPrefix = 'RefreshTokenAUID'
const tokenValidToPrefix = 'TokenValidToAUID'
const sessionHintPrefix = 'SessionAUID'

export const SetDefaultLanguage = () => {
  const cookie = Cookie.get('language')
  if (!cookie) {
    const language = languages.find(lang => lang.code === 'tr')
    Cookie.set('language', JSON.stringify(language), { path: '/' })
  }
}

export const SetLanguage = (code: string) => {
  const language = languages.find(language => language.code === code)
  Cookie.set('language', JSON.stringify(language), { path: '/' })
}

export const GetLanguage = () => {
  const cookie = Cookie.get('language')
  if (cookie) return JSON.parse(cookie)
  else return {}
}

export const GetToken = () => {
  const token = Cookie.get(prefix)
  if (token && localStorage.getItem(sessionHintPrefix) !== '1') {
    localStorage.setItem(sessionHintPrefix, '1')
  }
  return token ?? null
}

export const GetRefreshToken = () => {
  const token = Cookie.get(refreshPrefix)
  return token ?? null
}

export const HasSessionHint = () => localStorage.getItem(sessionHintPrefix) === '1'

export const GetTokenValidTo = () => {
  const validTo = Cookie.get(tokenValidToPrefix)
  return validTo ?? null
}

let sessionEnded = false
let sessionEndedLogoutClaimed = false

export const MarkSessionEnded = () => {
  sessionEnded = true
  sessionEndedLogoutClaimed = false
}

export const IsSessionEnded = () => sessionEnded

export const ClaimSessionEndedLogout = () => {
  if (!sessionEnded || sessionEndedLogoutClaimed) return false
  sessionEndedLogoutClaimed = true
  return true
}

export const SetToken = (token: string, tokenValidTo?: string) => {
  sessionEnded = false
  sessionEndedLogoutClaimed = false
  let expires: number | Date | undefined

  if (tokenValidTo) {
    const validToDate = new Date(tokenValidTo)
    if (!Number.isNaN(validToDate.getTime())) {
      expires = validToDate
      Cookie.set(tokenValidToPrefix, tokenValidTo, {
        expires: validToDate,
        sameSite: 'Lax',
        secure: !isLocal,
      })
    }
  }

  if (!expires) {
    const decoded = jwtDecode(token)
    if (decoded.exp) {
      expires = (decoded.exp * 1000 - Date.now()) / (1000 * 60 * 60 * 24)
    }
  }

  if (expires) {
    Cookie.set(prefix, token, {
      expires,
      sameSite: 'Lax',
      secure: !isLocal,
    })
  }
  localStorage.setItem(sessionHintPrefix, '1')
}

export const SetRefreshToken = (token: string, expiresAt?: string | Date) => {
  let expires: number | Date = 15 // Sliding but initially 15 days
  if (expiresAt) {
    const expireDate = new Date(expiresAt)
    if (!Number.isNaN(expireDate.getTime())) {
      expires = expireDate
    }
  }

  Cookie.set(refreshPrefix, token, {
    expires: expires,
    sameSite: 'Lax',
    secure: !isLocal,
  })
}

export const SetTokens = (
  token: string,
  refreshToken: string,
  tokenValidTo?: string,
  refreshTokenValidTo?: string,
) => {
  SetToken(token, tokenValidTo)
  SetRefreshToken(refreshToken, refreshTokenValidTo)
}

export const CheckToken = () => {
  const token = Cookie.get(prefix)
  const refreshToken = Cookie.get(refreshPrefix)

  if (!token) {
    return !!refreshToken || HasSessionHint() // Refresh token varsa oturumu interceptor yenileyecek
  }

  const decoded: JwtPayload = jwtDecode(token)
  if (decoded.exp && decoded.exp < Date.now() / 1000) {
    if (refreshToken || HasSessionHint()) {
      return true // Expired ama oturum ipucu varsa devam et
    }
    RemoveToken()
    return false
  }
  return true
}

export const RemoveToken = () => {
  Cookie.remove(prefix, {
    sameSite: 'Lax',
    secure: !isLocal,
  })
  Cookie.remove(refreshPrefix, {
    sameSite: 'Lax',
    secure: !isLocal,
  })
  Cookie.remove(tokenValidToPrefix, {
    sameSite: 'Lax',
    secure: !isLocal,
  })
  localStorage.removeItem(sessionHintPrefix)
}

export const SetMFAData = (data: any) => {
  const tenMinutes = new Date(Date.now() + 10 * 60 * 1000)
  Cookie.set('MFAData', JSON.stringify(data), {
    expires: tenMinutes,
  })
}

export const GetMFAData = () => {
  const data = Cookie.get('MFAData')
  return data ? JSON.parse(data) : null
}
